/**
 * The firm's order labels — *Багаж*, *Домашен любимец*, *Плащане с карта* — as it keeps them
 * (#462).
 *
 * A label is a vehicle feature: a dispatcher ticks it on an order, a passenger asks for it in
 * the app, and dispatch offers the order only to a car that has it. So a label is switched off
 * rather than deleted — cars and past orders name it by its code — and a starter keeps saying
 * its name in the reader's language until the firm types words of its own.
 *
 * Every control saves when it is pressed, as the firm's lines on the telephony page do: there
 * is nothing here worth a save bar, and a switch that waits for one is a switch somebody
 * leaves half-changed. The picture lives in the label's dialog rather than on the row, because
 * a row with three picture buttons beside two switches and two arrows is a row nobody reads.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  PageHeader,
  Pill,
  Section,
  Sub,
  Switch,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import { forgetFeatures } from '../../vehicles/featureNames';
import { featureName } from '../../vehicles/words';
import {
  labelPictureMaxBytes,
  labelsApi,
  type FeatureLabel,
  type FeatureLabelRequest,
} from '../api';
import { describe } from '../errors';
import { moved } from '../ordering';

/** The whole of a label as the API takes it back, with the changes laid over it. */
function bodyOf(label: FeatureLabel, changes: Partial<FeatureLabelRequest> = {}): FeatureLabelRequest {
  return {
    // A starter's own words sent back unchanged leave it unrenamed; only new words rename it.
    displayName: label.displayName,
    sortOrder: label.sortOrder,
    isActive: label.isActive,
    shownToRiders: label.shownToRiders,
    ...changes,
  };
}

export function LabelsPage() {
  const { t } = useTranslation();

  const [labels, setLabels] = useState<FeatureLabel[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** The dialog: a new label, or one being renamed and given a picture. Null when closed. */
  const [editing, setEditing] = useState<FeatureLabel | 'new' | null>(null);
  const [name, setName] = useState('');
  const [shownToRiders, setShownToRiders] = useState(true);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const firstBox = useRef<HTMLInputElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const nameOf = useCallback(
    (label: FeatureLabel) => featureName(t, label.code, label.displayName, label.namedByFirm),
    [t],
  );

  const load = useCallback(async () => {
    try {
      setLabels(await labelsApi.list());
    } catch (failure) {
      setError(describe(failure, t, 'settings.couldNotLoad'));
    }
  }, [t]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  // Only when the dialog opens, not each time a picture changes the label in it.
  const dialogOpen = editing !== null;

  useEffect(() => {
    if (!dialogOpen) return;

    // After showModal has done its own focusing, which it does on the same tick.
    const frame = requestAnimationFrame(() => firstBox.current?.focus());

    return () => cancelAnimationFrame(frame);
  }, [dialogOpen]);

  /** One label as the server now has it, in the list and in the dialog if it is open there. */
  function settle(label: FeatureLabel) {
    setLabels((current) => current?.map((row) => (row.id === label.id ? label : row)) ?? current);
    setEditing((current) => (current !== null && current !== 'new' && current.id === label.id ? label : current));
  }

  async function act(action: () => Promise<void>, inDialog = false) {
    setBusy(true);
    setError(null);
    setDialogError(null);

    try {
      await action();

      // So this console's order card says the new name without a reload.
      forgetFeatures();
    } catch (failure) {
      (inDialog ? setDialogError : setError)(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  function open(label: FeatureLabel | 'new') {
    setDialogError(null);
    setName(label === 'new' ? '' : nameOf(label));
    setShownToRiders(label === 'new' ? true : label.shownToRiders);
    setEditing(label);
  }

  function save() {
    const typed = name.trim();

    // Enter in the box reaches here as well as the button, which is disabled while busy.
    if (busy || editing === null || typed === '') return;

    void act(async () => {
      if (editing === 'new') {
        const created = await labelsApi.add({ displayName: typed, isActive: true, shownToRiders });

        await load();

        // Kept open on the new label, so its picture can be chosen without finding it again —
        // unless the dialog was closed, or opened on another label, while the request ran.
        setEditing((open) => (open === 'new' ? created : open));

        return;
      }

      // The translated name, left as it was, is not a rename: the starter keeps following the
      // reader's language rather than being pinned to one.
      const displayName = typed === nameOf(editing) ? editing.displayName : typed;

      settle(await labelsApi.save(editing.id, bodyOf(editing, { displayName })));
      setEditing((open) => (open !== null && open !== 'new' && open.id === editing.id ? null : open));
    }, true);
  }

  function move(index: number, step: -1 | 1) {
    if (labels === null) return;

    const changes = moved(labels, index, step);

    void act(async () => {
      try {
        for (const change of changes) {
          const label = labels.find((row) => row.id === change.id);

          if (label !== undefined) {
            await labelsApi.save(label.id, bodyOf(label, { sortOrder: change.sortOrder }));
          }
        }
      } finally {
        // Also after a refusal halfway through: some rows moved and some did not, and the
        // screen shows what the server now has rather than what was meant.
        await load();
      }
    });
  }

  function choosePicture(label: FeatureLabel, file: File) {
    void act(async () => {
      try {
        settle(await labelsApi.uploadPicture(label.id, file));
      } finally {
        // Cleared so choosing the same file again still fires a change event.
        if (fileInput.current !== null) fileInput.current.value = '';
      }
    }, true);
  }

  const current = editing !== null && editing !== 'new' ? editing : null;

  return (
    <>
      <PageHeader title={t('nav.settings.labels')} description={t('nav.settingsAbout.labels')} />

      <Section
        title={t('labels.title')}
        description={t('labels.help')}
        actions={
          <Button size="sm" disabled={busy} onClick={() => open('new')}>
            <Icon name="plus" small />
            {t('labels.add')}
          </Button>
        }
      >
        {error !== null && (
          <div className="px-5 pt-3">
            <Banner tone="danger">{error}</Banner>
          </div>
        )}

        {labels === null ? (
          error === null && <EmptyState>{t('app.loading')}</EmptyState>
        ) : labels.length === 0 ? (
          <EmptyState>{t('labels.none')}</EmptyState>
        ) : (
          <Table tight>
            <thead>
              <Tr>
                <Th>{t('labels.picture')}</Th>
                <Th>{t('labels.name')}</Th>
                <Th>{t('labels.shownToRiders')}</Th>
                <Th>{t('labels.active')}</Th>
                <Th>{t('labels.order')}</Th>
                <Th />
              </Tr>
            </thead>
            <tbody>
              {labels.map((label, index) => (
                <Tr key={label.id}>
                  <Td>
                    <Picture label={label} />
                  </Td>
                  <Td wrap>
                    <span className={label.isActive ? '' : 'text-ink-3'}>{nameOf(label)}</span>
                    {label.isStarter && !label.namedByFirm && (
                      <Sub block>{t('labels.translated')}</Sub>
                    )}
                  </Td>
                  <Td>
                    <Switch
                      checked={label.shownToRiders}
                      disabled={busy}
                      label={t('labels.shownToRidersOf', { name: nameOf(label) })}
                      onChange={(next) =>
                        void act(async () =>
                          settle(await labelsApi.save(label.id, bodyOf(label, { shownToRiders: next }))),
                        )
                      }
                    />
                  </Td>
                  <Td>
                    <Switch
                      checked={label.isActive}
                      disabled={busy}
                      label={t('labels.activeOf', { name: nameOf(label) })}
                      onChange={(next) =>
                        void act(async () =>
                          settle(await labelsApi.save(label.id, bodyOf(label, { isActive: next }))),
                        )
                      }
                    />
                  </Td>
                  <Td>
                    <span className="flex gap-1">
                      <Button
                        size="sm"
                        icon
                        disabled={busy || index === 0}
                        aria-label={t('settings.moveUp')}
                        onClick={() => move(index, -1)}
                      >
                        <Icon name="arrow-up" small />
                      </Button>
                      <Button
                        size="sm"
                        icon
                        disabled={busy || index === labels.length - 1}
                        aria-label={t('settings.moveDown')}
                        onClick={() => move(index, 1)}
                      >
                        <Icon name="arrow-down" small />
                      </Button>
                    </span>
                  </Td>
                  <Td>
                    <span className="flex justify-end">
                      <Button size="sm" disabled={busy} onClick={() => open(label)}>
                        {t('labels.edit')}
                      </Button>
                    </span>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Dialog
        open={editing !== null}
        title={current === null ? t('labels.add') : t('labels.editTitle', { name: nameOf(current) })}
        onClose={() => setEditing(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setEditing(null)}>
              {current === null ? t('settings.cancel') : t('settings.close')}
            </Button>
            <Button variant="primary" disabled={busy || name.trim() === ''} onClick={save}>
              {current === null ? t('labels.add') : t('settings.save')}
            </Button>
          </>
        }
      >
        <div
          className="flex flex-col gap-3.5"
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !(e.target instanceof HTMLInputElement)) return;
            e.preventDefault();
            save();
          }}
        >
          {dialogError !== null && <Banner tone="danger">{dialogError}</Banner>}

          <Field
            id="displayName"
            label={t('labels.name')}
            help={
              current?.isStarter && !current.namedByFirm ? t('labels.nameStarterHelp') : t('labels.nameHelp')
            }
          >
            <Input
              ref={firstBox}
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          {current === null ? (
            <>
              <label className="flex items-center gap-2.5 text-secondary">
                <Switch
                  checked={shownToRiders}
                  label={t('labels.shownToRiders')}
                  onChange={setShownToRiders}
                />
                {t('labels.shownToRidersHelp')}
              </label>
              <p className="m-0 text-secondary text-ink-3">{t('labels.pictureAfterAdding')}</p>
            </>
          ) : (
            // Not a `Field`: there is no one control here for a label to point at.
            <div className="flex flex-col">
              <span className="text-secondary font-medium text-ink-2 mb-1">{t('labels.picture')}</span>
              <div className="flex items-center gap-3 flex-wrap">
                <Picture label={current} large />

                {/* Hidden rather than styled, as the logo's is: the button is the control. */}
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  aria-label={t('labels.choosePicture')}
                  className="hidden"
                  disabled={busy}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file !== undefined) choosePicture(current, file);
                  }}
                />

                <Button size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
                  {current.picture === 'Own' ? t('labels.replacePicture') : t('labels.choosePicture')}
                </Button>

                {current.picture !== 'None' && (
                  <Button
                    size="sm"
                    variant="quiet"
                    disabled={busy}
                    onClick={() =>
                      void act(async () => settle(await labelsApi.removePicture(current.id)), true)
                    }
                  >
                    {t('labels.removePicture')}
                  </Button>
                )}

                {current.isStarter && current.picture !== 'Default' && (
                  <Button
                    size="sm"
                    variant="quiet"
                    disabled={busy}
                    onClick={() =>
                      void act(async () => settle(await labelsApi.removePicture(current.id, true)), true)
                    }
                  >
                    {t('labels.defaultPicture')}
                  </Button>
                )}
              </div>
              <span className="text-secondary text-ink-3 mt-1">
                {t('labels.pictureHelp', { kb: labelPictureMaxBytes / 1024 })}
              </span>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}

/** The label's picture, or the words that say it is shown in words alone. */
function Picture({ label, large = false }: { label: FeatureLabel; large?: boolean }) {
  const { t } = useTranslation();

  if (label.pictureUrl === null) {
    return <Pill tone="neutral">{t('labels.wordsAlone')}</Pill>;
  }

  return (
    // In the table the mark goes under the picture, which keeps the column narrow enough for
    // the table to fit the settings page; in the dialog there is room beside it.
    <span className={large ? 'inline-flex items-center gap-2' : 'inline-flex flex-col items-start gap-0.5'}>
      <img
        // Same origin, as the logo is; the URL carries its own version, so a new picture is a
        // new address and the browser never shows the old one.
        src={label.pictureUrl}
        alt=""
        className={large ? 'h-16 w-16 object-contain' : 'h-8 w-8 object-contain'}
      />
      {label.picture === 'Default' && <Sub>{t('labels.defaultMark')}</Sub>}
    </span>
  );
}
